import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Leaf, Sprout, Scissors, Grid3x3, TreePine, Mountain, Square, Sparkles, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import { useSettings } from "@/context/AppContext";
import { readCache, writeCache } from "@/lib/cache";
import SmartImage from "@/components/SmartImage";
import ImageSkeleton from "@/components/ImageSkeleton";

const ICONS = { Leaf, Sprout, Scissors, Grid3x3, TreePine, Mountain, Square, Sparkles };

export default function ServicesPage() {
  const cached = readCache("services", []);
  const [services, setServices] = useState(cached);
  const [loaded, setLoaded] = useState(false); // toujours attendre l'API avant d'afficher les images
  const { settings } = useSettings();

  useEffect(() => {
    api.get("/services").then((r) => {
      const data = r.data || [];
      setServices(data);
      writeCache("services", data);
      setLoaded(true);
    });
  }, []);

  return (
    <PublicLayout>
      <section className="pt-36 pb-12 container-px max-w-7xl mx-auto">
        <p className="label-eyebrow mb-5">Services</p>
        <h1 className="font-display text-5xl md:text-7xl text-forest font-light tracking-tight max-w-3xl leading-[0.95]">
          Création, entretien et aménagement.
        </h1>
        <p className="mt-8 max-w-xl text-forest-soft leading-relaxed">
          Une prestation complète pour donner vie à votre jardin et le maintenir au plus haut niveau, tout au long de l'année.
        </p>
      </section>

      <section className="container-px max-w-7xl mx-auto pb-32">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-16 md:gap-y-20">
          {!loaded ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="grid grid-cols-1 sm:grid-cols-12 gap-6">
                <ImageSkeleton className="sm:col-span-5 aspect-[4/5]" />
                <div className="sm:col-span-7 space-y-3 pt-4">
                  <div className="h-3 bg-sand w-12" />
                  <div className="h-8 bg-sand w-3/4" />
                  <div className="h-3 bg-sand w-full" />
                  <div className="h-3 bg-sand w-4/5" />
                </div>
              </div>
            ))
          ) : (
            services.map((s, i) => {
              const Icon = ICONS[s.icon] || Leaf;
              return (
                <article
                  key={s.id}
                  className="group grid grid-cols-1 sm:grid-cols-12 gap-6"
                  data-testid={`service-detail-${i}`}
                >
                  <div className="sm:col-span-5 aspect-[4/5] overflow-hidden">
                    <SmartImage
                      src={s.image}
                      alt={s.title}
                      className="w-full h-full group-hover:scale-105 transition duration-1000"
                      width={900}
                    />
                  </div>
                  <div className="sm:col-span-7 flex flex-col justify-center">
                    <div className="flex items-center gap-3 mb-4">
                      <Icon className="w-5 h-5 text-corten" strokeWidth={1.5} />
                      <span className="label-eyebrow">0{i + 1}</span>
                    </div>
                    <h2 className="font-display text-3xl md:text-4xl text-forest font-light tracking-tight leading-tight">
                      {s.title}
                    </h2>
                    <p className="mt-5 text-forest-soft leading-relaxed">{s.description}</p>
                    <Link to="/contact" className="inline-flex items-center gap-2 mt-7 text-forest link-underline text-sm self-start">
                      Demander un devis <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                </article>
              );
            })
          )}
        </div>
      </section>

      <section className="bg-bone py-24">
        <div className="container-px max-w-5xl mx-auto text-center">
          <h2 className="font-display text-4xl md:text-5xl text-forest font-light">
            Une question, une idée, un projet ?
          </h2>
          <Link to="/contact" className="inline-flex items-center gap-2 mt-8 btn-primary">
            {settings?.cta_label || "Demander un devis"} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
}
